/**
 * Session Create Page
 *
 * Phase 1 task 4: writers create a live session through this form, then see a
 * confirmation card with the public join URL + QR code. They can copy the
 * link, scan the QR, or click "Open writer" to start streaming.
 *
 * The Phase 1 plan says: "On success: navigate to writer interface that uses
 * the existing writer-mock components, OR if too coupled, render the QR code
 * + a 'session created' confirmation". The existing `/writer` route is
 * guarded by `RoomParamsGuard` which requires BOTH documentId and roomId —
 * sessions created without a roomId can't enter that flow directly. The
 * confirmation card sidesteps the coupling: it deep-links to `/writer` only
 * when a roomId is present, otherwise the join URL is the primary affordance.
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { SessionRole } from '@polygraf/shared'
import { ArrowLeft, ArrowRight, Check, Copy, FileText, Monitor, Play, Shield } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { AuthGuard } from '../components/auth-guard'
import {
	Alert,
	Button,
	buttonClasses,
	Checkbox,
	cn,
	IconButton,
	Spinner,
	TextField,
} from '../components/ui'
import { usePageTitle } from '../hooks/use-page-title'
import { useUserRole } from '../hooks/use-user-role'
import { trpc } from '../trpc/client'

// MP-191: testers couldn't figure out how to start a session with BOTH a live
// transcript and a screen broadcast — the form silently hard-coded the WRITER
// role. The two feature checkboxes make the combination explicit; at least one
// must stay selected.
const createSchema = (t: (key: string) => string) =>
	z
		.object({
			title: z
				.string()
				.trim()
				.min(1, t('sessions.create.errors.titleRequired'))
				.max(200, t('sessions.create.errors.titleTooLong')),
			roomId: z.string().optional(),
			withTranscript: z.boolean(),
			withScreenShare: z.boolean(),
		})
		.refine((value) => value.withTranscript || value.withScreenShare, {
			message: t('sessions.create.errors.featureRequired'),
			path: ['withTranscript'],
		})

type FormData = z.infer<ReturnType<typeof createSchema>>

interface CreatedSession {
	id: string
	documentId: string
	// r65: 6-char dictatable join code; null only for pre-r65 sessions.
	shortCode: string | null
	title: string
	roomId: string | null
	hasTranscript: boolean
	hasVideo: boolean
}

function getJoinUrl(sessionId: string): string {
	return `${window.location.origin}/join/${sessionId}`
}

function SessionCreateContent() {
	const { t } = useTranslation()
	const { isPresenter, isLoading: roleLoading } = useUserRole()
	usePageTitle(
		!roleLoading && !isPresenter
			? t('sessions.create.presenterRequiredTitle')
			: t('sessions.create.title'),
	)
	const [searchParams] = useSearchParams()
	const prefilledRoomId = searchParams.get('roomId')?.trim() || undefined
	const [submitError, setSubmitError] = useState<string | null>(null)
	const [created, setCreated] = useState<CreatedSession | null>(null)
	const [copied, setCopied] = useState(false)
	const [copyFailed, setCopyFailed] = useState(false)

	const form = useForm<FormData>({
		resolver: zodResolver(createSchema(t)),
		defaultValues: {
			title: '',
			roomId: prefilledRoomId ?? '',
			withTranscript: true,
			withScreenShare: false,
		},
	})

	const createMutation = trpc.sessions.create.useMutation({
		onSuccess: (data) => {
			setCreated({
				id: data.id,
				documentId: data.documentId,
				shortCode: data.shortCode,
				title: data.title,
				roomId: data.roomId,
				hasTranscript: data.hasTranscript,
				hasVideo: data.hasVideo,
			})
		},
		onError: (err) => {
			setSubmitError(err.message || t('sessions.create.errors.createFailed'))
		},
	})

	const onSubmit = form.handleSubmit((values) => {
		setSubmitError(null)
		const trimmedTitle = values.title.trim()
		const trimmedRoomId = values.roomId?.trim()
		// WRITER drives the transcript, PRESENTER the LiveKit screen broadcast;
		// both at once is the MP-191 "transcript + broadcast" session.
		const roles = [
			...(values.withTranscript ? [SessionRole.WRITER] : []),
			...(values.withScreenShare ? [SessionRole.PRESENTER] : []),
		]
		createMutation.mutate({
			title: trimmedTitle,
			roles,
			roomId: trimmedRoomId ? trimmedRoomId : undefined,
		})
	})

	// r65: the displayed/copied/QR-encoded URL prefers the dictatable short
	// code; the cuid stays the fallback for sessions without one.
	const joinUrl = created ? getJoinUrl(created.shortCode ?? created.id) : ''

	const handleCopy = useCallback(async () => {
		if (!joinUrl) return
		try {
			await navigator.clipboard.writeText(joinUrl)
			setCopied(true)
			setCopyFailed(false)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			// Clipboard API unavailable / denied — tell the user to copy manually
			// instead of failing silently.
			setCopyFailed(true)
		}
	}, [joinUrl])

	if (roleLoading) {
		return (
			<div className="max-w-2xl mx-auto px-4 py-12 flex justify-center text-primary">
				<Spinner size="lg" />
			</div>
		)
	}

	// Server requires PRESENTER+ on `sessions.create`. Mirror the gate at the
	// route level so non-presenters see an inline access-denied card instead
	// of filling out a form that fails with an untranslated server error.
	if (!isPresenter) {
		return (
			<div className="max-w-2xl mx-auto space-y-4 px-4 py-8 animate-enter">
				<Link
					to="/sessions"
					className={cn(
						buttonClasses({ variant: 'ghost', size: 'sm' }),
						'inline-flex items-center gap-2 w-fit',
					)}
				>
					<ArrowLeft size={18} />
					{t('common.back')}
				</Link>
				<div className="card-refined p-12 text-center">
					<Shield size={48} className="mx-auto mb-4 opacity-30" />
					<p className="text-lg font-medium">{t('sessions.create.presenterRequiredTitle')}</p>
					<p className="text-base-content/60 mt-2">{t('sessions.create.presenterRequiredDesc')}</p>
				</div>
			</div>
		)
	}

	if (created) {
		// Build the writer URL only when the session has a transcript AND we
		// have both ids — `/writer` is guarded by RoomParamsGuard.
		const writerHref =
			created.hasTranscript && created.roomId
				? `/writer?${new URLSearchParams({
						documentId: created.documentId,
						title: created.title,
						roomId: created.roomId,
					}).toString()}`
				: null
		// Screen sharing happens on the presenter page; it only needs the
		// session id (the LiveKit room is derived from it server-side).
		const presentHref = created.hasVideo ? `/sessions/${created.id}/present` : null

		return (
			<div className="space-y-6 animate-enter max-w-2xl mx-auto">
				<div className="flex items-center gap-3">
					<Link
						to="/sessions"
						className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
						aria-label={t('common.back')}
					>
						<ArrowLeft size={20} />
					</Link>
					<div>
						<h1 className="text-2xl font-display font-semibold">
							{t('sessions.create.success.title')}
						</h1>
						<p className="text-base-content/60 mt-1">
							{t('sessions.create.success.subtitle', { title: created.title })}
						</p>
					</div>
				</div>

				<div className="card-refined p-6 space-y-5">
					<div className="space-y-2">
						<label htmlFor="join-url" className="text-sm font-medium block">
							{t('sessions.create.success.joinUrl')}
						</label>
						<div className="flex items-center gap-2">
							<code id="join-url" className="text-sm bg-base-200 px-3 py-2 rounded flex-1 truncate">
								{joinUrl}
							</code>
							<IconButton
								variant="ghost"
								size="sm"
								onClick={handleCopy}
								aria-label={copied ? t('common.copied') : t('common.copy')}
							>
								{copied ? <Check className="text-success" /> : <Copy />}
							</IconButton>
						</div>
						{copyFailed && (
							<p role="alert" className="text-xs text-error mt-2">
								{t('sessions.create.success.copyManual')}
							</p>
						)}
					</div>

					<div className="flex justify-center py-4">
						<div className="bg-white p-4 rounded-xl shadow-soft">
							<QRCodeSVG
								value={joinUrl}
								size={220}
								level="M"
								title={t('sessions.create.success.qrAlt')}
							/>
						</div>
					</div>

					<p className="text-sm text-base-content/60 text-center">
						{t('sessions.create.success.qrHint')}
					</p>

					<div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
						<Link to={`/join/${created.id}`}>
							<Button type="button" variant="ghost" block>
								{t('sessions.create.success.viewJoin')}
							</Button>
						</Link>
						{writerHref && (
							<Link to={writerHref}>
								<Button type="button" variant="primary" block leadingIcon={<Play size={16} />}>
									{t('sessions.create.success.openWriter')}
								</Button>
							</Link>
						)}
						{presentHref && (
							<Link to={presentHref}>
								<Button type="button" variant="primary" block leadingIcon={<Monitor size={16} />}>
									{t('sessions.create.success.openPresenter')}
								</Button>
							</Link>
						)}
					</div>

					{/* The writer surface needs a room id (RoomParamsGuard) — explain
					    instead of silently dropping the button. */}
					{created.hasTranscript && !created.roomId && (
						<Alert tone="info" className="text-sm">
							{t('sessions.create.success.writerRequiresRoom')}
						</Alert>
					)}

					{/* MP-191: combined sessions drive two surfaces — spell out how. */}
					{writerHref && presentHref && (
						<p className="text-sm text-base-content/60">{t('sessions.create.success.bothHint')}</p>
					)}
				</div>

				<div className="flex justify-end">
					<Link
						to="/sessions"
						className="text-sm text-primary hover:underline inline-flex items-center gap-1"
					>
						{t('sessions.create.success.viewAll')}
						<ArrowRight size={14} />
					</Link>
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-6 animate-enter max-w-2xl mx-auto">
			<div className="flex items-center gap-3">
				<Link
					to="/sessions"
					className={cn(buttonClasses({ variant: 'ghost', size: 'sm' }), 'p-2 rounded-xl')}
					aria-label={t('common.back')}
				>
					<ArrowLeft size={20} />
				</Link>
				<div>
					<h1 className="text-2xl font-display font-semibold">{t('sessions.create.title')}</h1>
					<p className="text-base-content/60 mt-1">{t('sessions.create.subtitle')}</p>
				</div>
			</div>

			<form onSubmit={onSubmit} className="card-refined p-6 space-y-4">
				<TextField
					id="session-title"
					label={t('sessions.create.fields.title')}
					placeholder={t('sessions.create.fields.titlePlaceholder')}
					leadingIcon={<FileText size={18} />}
					autoFocus
					required
					maxLength={200}
					error={form.formState.errors.title?.message}
					{...form.register('title')}
				/>

				{/* MP-191: explicit feature selection — transcript, screen sharing,
				    or both. Replaces the invisible hard-coded WRITER role. */}
				<fieldset>
					<legend className="text-sm font-medium">{t('sessions.create.fields.features')}</legend>
					<p className="text-xs text-base-content/60 mt-1 mb-3">
						{t('sessions.create.fields.featuresHint')}
					</p>
					<div className="grid sm:grid-cols-2 gap-3">
						<label
							htmlFor="feature-transcript"
							className="flex items-start gap-3 rounded-xl border border-base-300 p-4 cursor-pointer transition-colors hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
						>
							<Checkbox id="feature-transcript" {...form.register('withTranscript')} />
							<span className="min-w-0">
								<span className="flex items-center gap-1.5 text-sm font-medium">
									<FileText size={15} className="text-primary" aria-hidden="true" />
									{t('sessions.create.fields.featureTranscript')}
								</span>
								<span className="block text-xs text-base-content/60 mt-1">
									{t('sessions.create.fields.featureTranscriptHint')}
								</span>
							</span>
						</label>
						<label
							htmlFor="feature-screen-share"
							className="flex items-start gap-3 rounded-xl border border-base-300 p-4 cursor-pointer transition-colors hover:border-primary/50 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
						>
							<Checkbox id="feature-screen-share" {...form.register('withScreenShare')} />
							<span className="min-w-0">
								<span className="flex items-center gap-1.5 text-sm font-medium">
									<Monitor size={15} className="text-sage-600" aria-hidden="true" />
									{t('sessions.create.fields.featureScreenShare')}
								</span>
								<span className="block text-xs text-base-content/60 mt-1">
									{t('sessions.create.fields.featureScreenShareHint')}
								</span>
							</span>
						</label>
					</div>
					{form.formState.errors.withTranscript?.message && (
						<p role="alert" className="text-xs text-error mt-2">
							{form.formState.errors.withTranscript.message}
						</p>
					)}
				</fieldset>

				<TextField
					id="session-room"
					label={t('sessions.create.fields.roomId')}
					placeholder={t('sessions.create.fields.roomIdPlaceholder')}
					hint={t('sessions.create.fields.roomIdHint')}
					error={form.formState.errors.roomId?.message}
					{...form.register('roomId')}
				/>

				{submitError && <Alert tone="error">{submitError}</Alert>}

				<div className="flex justify-end gap-2 pt-2">
					<Link to="/sessions">
						<Button type="button" variant="ghost">
							{t('common.cancel')}
						</Button>
					</Link>
					<Button
						type="submit"
						variant="primary"
						loading={createMutation.isPending}
						leadingIcon={<Play size={16} />}
					>
						{t('sessions.create.submit')}
					</Button>
				</div>
			</form>
		</div>
	)
}

export default function SessionCreatePage() {
	return (
		<AuthGuard>
			<SessionCreateContent />
		</AuthGuard>
	)
}
